import DOMPurify from 'dompurify';

/**
 * Tags produced by Quill (see quillUtils.ts `formats` + node_modules/quill/formats):
 * header, bold, italic, underline, strike, color, background, list, indent,
 * link, video, image, script (sub/super), mention.
 */
const ALLOWED_TAGS = [
  // Block / structure
  'p', 'br', 'div', 'blockquote', 'pre',
  // Headings (Quill header format)
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Inline text (bold/italic/underline/strike + react-simple-wysiwyg)
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'span', 'small',
  // Lists & indent (ql-indent-* on li/p via class)
  'ul', 'ol', 'li',
  // Links, media (Quill link / image / video)
  'a', 'img', 'iframe',
  // Tables (LLM / API HTML)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'code', 'hr',
] as const;

/**
 * Attributes used by Quill blots and CustomImageBlot / CustomLinkBlot.
 * `style` is required for color/background; DOMPurify sanitizes CSS values.
 */
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'class',
  'style',
  'src', 'alt', 'width', 'height',
  'frameborder', 'allowfullscreen', 'allow', 'scrolling',
  // quill-mention (MentionBlot dataset → data-* on span.mention)
  'data-id', 'data-value', 'data-denotation-char',
] as const;

let quillIframeHookRegistered = false;

const registerQuillSanitizeHooks = () => {
  if (quillIframeHookRegistered) return;
  quillIframeHookRegistered = true;

  // Quill video embeds: only https (or protocol-relative) iframe sources
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const src = (node as Element).getAttribute('src')?.trim() ?? '';
    const safe =
      /^https:\/\//i.test(src) ||
      /^\/\//.test(src) ||
      src === '';
    if (!safe) {
      node.parentNode?.removeChild(node);
    }
  });
};

registerQuillSanitizeHooks();

/** Sanitize HTML before dangerouslySetInnerHTML (Quill, LLM output, API content, translations). */
export const sanitizeHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ALLOW_DATA_ATTR: false,
  });
